// UC 34255 — Pull Order Entry & Status Page
// Mirrors RawHTMLDesign/brand-pull-order-management/pull-order-entry-&-status-page.html
// Role: Outlet Manager (submit) | Commissary Supervisor / Brand Manager (view + status update)
// All mutating controls carry data-testid for Playwright.

import { useEffect, useMemo, useState } from 'react';
import api from '../../../services/api.js';
import { MEAL_PERIODS } from '../../../utils/mealPeriods.js';
import { useAuth } from '../../../utils/AuthContext.jsx';
import { ROLES } from '../../../utils/roles.js';

const STATUS_OPTIONS = ['Submitted', 'Acknowledged', 'In Production', 'Dispatched', 'Received', 'Rejected', 'Cancelled'];

const EMPTY_FORM = () => ({
  outlet_id: '',
  brand_id: '',
  cadence: 'Daily',
  delivery_date: '',
  meal_period: '',   // optional daypart this order is for
  special_instructions: '',
  items: [],       // { item_id, quantity, unit }
});

function statusBadgeClass(s) {
  switch (s) {
    case 'Submitted': return 'badge-primary';
    case 'Acknowledged': return 'badge-info';
    case 'In Production': return 'badge-warning';
    case 'Dispatched': return 'badge-teal';
    case 'Received': return 'badge-success';
    case 'Rejected': return 'badge-danger';
    case 'Cancelled': return 'badge-secondary';
    default: return 'badge-light';
  }
}

export default function PullOrderEntryAndStatusPage() {
  const { user, hasRole } = useAuth();
  const isManager = hasRole(ROLES.OUTLET_MANAGER, ROLES.SUPER_ADMIN);
  const isSupervisor = hasRole(ROLES.COMMISSARY_SUPERVISOR, ROLES.SUPER_ADMIN, ROLES.FINANCE, ROLES.BRAND_MANAGER);
  // Brand Manager: read-only view filtered to their assigned brands
  const isBrandManager = hasRole(ROLES.BRAND_MANAGER) && !hasRole(ROLES.SUPER_ADMIN, ROLES.FINANCE);
  const brandManagerBrandIds = isBrandManager ? (user?.brand_ids || []) : [];

  const [outlets, setOutlets] = useState([]);
  const [brands, setBrands] = useState([]);
  const [items, setItems] = useState([]);
  const [orders, setOrders] = useState([]);

  const [form, setForm] = useState(EMPTY_FORM());
  const [draftItems, setDraftItems] = useState([]);    // items added to the draft list
  const [selectedSku, setSelectedSku] = useState('');
  const [selectedQty, setSelectedQty] = useState(1);

  const [banner, setBanner] = useState({ message: '', kind: '' });
  const [submitting, setSubmitting] = useState(false);

  // Filter state for history panel
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [search, setSearch] = useState('');

  // Detail modal
  const [modalOrder, setModalOrder] = useState(null);
  const [supervisorStatus, setSupervisorStatus] = useState('Acknowledged');
  const [statusUpdating, setStatusUpdating] = useState(false);

  // Lifecycle action state (UC 34271 / 34273 / 34274 / 34275)
  const [rejectReason, setRejectReason] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [lifecycleError, setLifecycleError] = useState('');
  const [lifecycleLoading, setLifecycleLoading] = useState(false);

  async function refresh() {
    const [outRes, brandRes, itemRes, orderRes] = await Promise.all([
      api.get('/master-data/outlets').then((x) => x.data.data || []).catch(() => []),
      api.get('/master-data/brands').then((x) => x.data.data || []).catch(() => []),
      api.get('/master-data/items').then((x) => (x.data.data || []).filter((i) => i.is_active !== false)).catch(() => []),
      api.get('/pull-orders').then((x) => x.data.data || []).catch(() => []),
    ]);
    setOutlets(outRes);
    setBrands(brandRes);
    setItems(itemRes);
    setOrders(orderRes);
  }

  useEffect(() => { refresh(); }, []);

  const itemById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);

  function setField(name, value) { setForm((f) => ({ ...f, [name]: value })); }

  function addSkuToDraft() {
    if (!selectedSku) return;
    const qty = parseInt(selectedQty, 10);
    if (!qty || qty <= 0) {
      setBanner({ message: 'Quantity must be a whole number greater than zero.', kind: 'error' });
      return;
    }
    const existing = draftItems.findIndex((d) => d.item_id === selectedSku);
    if (existing >= 0) {
      setDraftItems((prev) => prev.map((d, i) => i === existing ? { ...d, quantity: d.quantity + qty } : d));
    } else {
      const it = itemById[selectedSku];
      setDraftItems((prev) => [...prev, { item_id: selectedSku, quantity: qty, unit: it?.uom || '' }]);
    }
    setSelectedSku('');
    setSelectedQty(1);
    setBanner({ message: '', kind: '' });
  }

  function removeDraftItem(idx) {
    setDraftItems((prev) => prev.filter((_, i) => i !== idx));
  }

  const totalQty = draftItems.reduce((s, d) => s + d.quantity, 0);

  async function submitOrder() {
    setBanner({ message: '', kind: '' });
    if (!form.outlet_id) { setBanner({ message: 'Please select an outlet.', kind: 'error' }); return; }
    if (!form.delivery_date) { setBanner({ message: 'Please select a delivery date.', kind: 'error' }); return; }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const dDate = new Date(form.delivery_date);
    if (dDate < today) { setBanner({ message: 'Delivery date must not be in the past.', kind: 'error' }); return; }
    if (!draftItems.length) { setBanner({ message: 'Add at least one SKU with quantity > 0.', kind: 'error' }); return; }

    const outlet = outlets.find((o) => o.id === form.outlet_id);
    const brand_id = outlet?.brand_id || (brands[0]?.id || '');

    setSubmitting(true);
    try {
      const res = await api.post('/pull-orders', {
        outlet_id: form.outlet_id,
        brand_id,
        cadence: form.cadence,
        delivery_date: form.delivery_date,
        meal_period: form.meal_period || undefined,
        special_instructions: form.special_instructions || null,
        items: draftItems,
      });
      const order = res.data.data;
      let msg = `Order submitted successfully. Status: ${order.status}.`;
      if (order.has_duplicate_warning) msg += ` Warning: duplicate order detected (ref: ${order.duplicate_ref}).`;
      if (order.has_late_warning) msg += ' Warning: submitted after cut-off time.';
      if (order.has_no_cutoff_warning) msg += ' Warning: no cut-off rule configured — filed as administrative exception.';
      setBanner({ message: msg, kind: 'success' });
      setForm(EMPTY_FORM());
      setDraftItems([]);
      await refresh();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Submission failed.', kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  const filteredOrders = orders.filter((o) => {
    // Brand Manager sees only orders belonging to their assigned brands
    if (isBrandManager && brandManagerBrandIds.length > 0 && !brandManagerBrandIds.includes(o.brand_id)) return false;
    if (filterStatus !== 'ALL' && o.status !== filterStatus) return false;
    if (filterDateStart && o.delivery_date < filterDateStart) return false;
    if (filterDateEnd && o.delivery_date > filterDateEnd) return false;
    if (search) {
      const hay = `${o.id} ${(o.items || []).map((i) => i.item_code + ' ' + i.item_name).join(' ')}`.toLowerCase();
      if (!hay.includes(search.toLowerCase())) return false;
    }
    return true;
  });

  async function openDetail(order) {
    setModalOrder(order);
    setSupervisorStatus('Acknowledged');
    setRejectReason('');
    setCancelReason('');
    setLifecycleError('');
  }

  async function saveStatusUpdate() {
    if (!modalOrder) return;
    setStatusUpdating(true);
    try {
      const res = await api.patch(`/pull-orders/${modalOrder.id}/status`, { status: supervisorStatus });
      setModalOrder(res.data.data);
      await refresh();
    } catch (err) {
      console.error('Status update failed', err);
    } finally {
      setStatusUpdating(false);
    }
  }

  // UC 34271 — Acknowledge
  async function acknowledgeOrder() {
    setLifecycleLoading(true); setLifecycleError('');
    try {
      const res = await api.patch(`/pull-orders/${modalOrder.id}/acknowledge`);
      setModalOrder(res.data.data);
      await refresh();
    } catch (err) {
      setLifecycleError(err?.response?.data?.error || 'Acknowledge failed.');
    } finally { setLifecycleLoading(false); }
  }

  // UC 34273 — Reject
  async function rejectOrder() {
    if (!rejectReason.trim()) { setLifecycleError('A rejection reason is required.'); return; }
    setLifecycleLoading(true); setLifecycleError('');
    try {
      const res = await api.patch(`/pull-orders/${modalOrder.id}/reject`, { rejection_reason: rejectReason });
      setModalOrder(res.data.data);
      setRejectReason('');
      await refresh();
    } catch (err) {
      setLifecycleError(err?.response?.data?.error || 'Reject failed.');
    } finally { setLifecycleLoading(false); }
  }

  // UC 34273 — Resubmit rejected order
  async function resubmitOrder() {
    setLifecycleLoading(true); setLifecycleError('');
    try {
      await api.post(`/pull-orders/${modalOrder.id}/resubmit`);
      setModalOrder(null);
      await refresh();
      setBanner({ message: 'A new draft order has been created from the rejected order.', kind: 'success' });
    } catch (err) {
      setLifecycleError(err?.response?.data?.error || 'Resubmit failed.');
    } finally { setLifecycleLoading(false); }
  }

  // UC 34275 — Cancel
  async function cancelOrder() {
    if (!cancelReason.trim()) { setLifecycleError('A cancellation reason is required.'); return; }
    setLifecycleLoading(true); setLifecycleError('');
    try {
      const res = await api.patch(`/pull-orders/${modalOrder.id}/cancel`, { cancellation_reason: cancelReason });
      setModalOrder(res.data.data);
      setCancelReason('');
      await refresh();
    } catch (err) {
      setLifecycleError(err?.response?.data?.error || 'Cancel failed.');
    } finally { setLifecycleLoading(false); }
  }

  return (
    <div className="container-fluid px-4 py-4" data-testid="pull-order-page">
      {/* Page Header */}
      <div className="row mb-4">
        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h1 className="h3 font-weight-bold text-dark mb-1">
              <i className="fa-solid fa-cart-flatbed mr-2 text-primary" />
              Outlet Pull Order Entry &amp; Tracking
            </h1>
            <p className="text-muted mb-0">Compile, submit, and track daily or weekly inventory requirements for your outlets.</p>
          </div>
          <span className="badge badge-light border px-3 py-2 text-dark shadow-sm mt-2 mt-sm-0">
            <i className="fa-solid fa-user-tag text-teal mr-1" />Role: <strong data-testid="current-role-badge">{user?.role || 'Outlet Manager'}</strong>
          </span>
        </div>
      </div>

      {/* Cut-off notice */}
      <div className="row mb-3">
        <div className="col-12">
          <div className="alert alert-info shadow-sm" style={{ borderLeft: '5px solid #008080' }} data-testid="cutoff-notice">
            <i className="fa-solid fa-circle-info mr-2 text-primary" />
            <strong>Standard Cut-off Notice:</strong> Orders must be submitted at least <strong>24 hours</strong> in advance. Operational cutoff is 4:00 PM daily.
          </div>
        </div>
      </div>

      {/* Banner */}
      {banner.message && (
        <div
          className={`alert alert-${banner.kind === 'error' ? 'danger' : 'success'} mb-3`}
          data-testid={banner.kind === 'error' ? 'order-error' : 'order-success'}
        >
          {banner.message}
        </div>
      )}

      {/* Brand Manager read-only indicator */}
      {isBrandManager && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-info shadow-sm" data-testid="brand-manager-view">
              <i className="fa-solid fa-eye mr-2 text-primary" />
              <strong>Brand Manager View:</strong> This is a read-only tracker showing orders for your assigned brands. Order submission and lifecycle actions are not available in this view.
            </div>
          </div>
        </div>
      )}

      <div className="row">
        {/* Left: Order Compilation — hidden for Brand Managers (read-only view) */}
        {!isBrandManager && <div className="col-xl-5 col-lg-6 mb-4">
          <div className="card h-100 shadow-sm">
            <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center border-bottom">
              <h5 className="card-title mb-0 font-weight-bold text-teal">
                <i className="fa-solid fa-pen-to-square mr-2" />Order Compilation Draft
              </h5>
              <span className="badge badge-secondary" data-testid="draft-item-count">{draftItems.length} SKUs Drafted</span>
            </div>
            <div className="card-body">
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="font-weight-600 text-dark">
                    Select Outlet <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control custom-select"
                    value={form.outlet_id}
                    onChange={(e) => setField('outlet_id', e.target.value)}
                    data-testid="outlet-select"
                  >
                    <option value="" disabled>Choose Outlet...</option>
                    {outlets.map((o) => (
                      <option key={o.id} value={o.id}>{o.name} ({o.code})</option>
                    ))}
                  </select>
                </div>
                <div className="form-group col-md-6">
                  <label className="font-weight-600 text-dark">
                    Delivery Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={form.delivery_date}
                    onChange={(e) => setField('delivery_date', e.target.value)}
                    data-testid="delivery-date"
                  />
                  <small className="text-muted">Must be today or future</small>
                </div>
              </div>

              <div className="form-group">
                <label className="font-weight-600 text-dark">Meal Period</label>
                <select
                  className="form-control custom-select"
                  value={form.meal_period}
                  onChange={(e) => setField('meal_period', e.target.value)}
                  data-testid="meal-period"
                >
                  <option value="">Unspecified</option>
                  {MEAL_PERIODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <small className="text-muted">
                  Which service period this demand is for. Optional, but required to track
                  consumption per cafeteria per meal period.
                </small>
              </div>

              <div className="form-group">
                <label className="font-weight-600 text-dark d-block">
                  Order Cadence <span className="text-danger">*</span>
                </label>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="cadence-daily"
                    name="cadence"
                    className="custom-control-input"
                    value="Daily"
                    checked={form.cadence === 'Daily'}
                    onChange={() => setField('cadence', 'Daily')}
                    data-testid="cadence-daily"
                  />
                  <label className="custom-control-label" htmlFor="cadence-daily">Daily Pull</label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="cadence-weekly"
                    name="cadence"
                    className="custom-control-input"
                    value="Weekly"
                    checked={form.cadence === 'Weekly'}
                    onChange={() => setField('cadence', 'Weekly')}
                    data-testid="cadence-weekly"
                  />
                  <label className="custom-control-label" htmlFor="cadence-weekly">Weekly Bulk Pull</label>
                </div>
              </div>

              <hr className="my-3" />

              {/* SKU Adder */}
              <div className="bg-light p-3 rounded mb-3">
                <h6 className="font-weight-bold text-dark mb-2">
                  <i className="fa-solid fa-cube mr-1 text-primary" />Add Recipe/Ingredient SKU
                </h6>
                <div className="form-row">
                  <div className="form-group col-md-7 mb-2">
                    <select
                      className="form-control custom-select"
                      value={selectedSku}
                      onChange={(e) => setSelectedSku(e.target.value)}
                      data-testid="sku-select"
                    >
                      <option value="" disabled>Search or Select SKU...</option>
                      {items.map((i) => (
                        <option key={i.id} value={i.id}>{i.item_code} - {i.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group col-md-3 mb-2">
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Qty"
                      min="1"
                      step="1"
                      value={selectedQty}
                      onChange={(e) => setSelectedQty(e.target.value)}
                      data-testid="sku-quantity"
                    />
                  </div>
                  <div className="col-md-2 mb-2">
                    <button
                      type="button"
                      className="btn btn-primary btn-block px-1"
                      onClick={addSkuToDraft}
                      data-testid="btn-add-sku"
                    >
                      <i className="fa-solid fa-plus" /> Add
                    </button>
                  </div>
                </div>
              </div>

              {/* Draft table */}
              <div className="table-responsive border rounded bg-white mt-2" style={{ maxHeight: 220, overflowY: 'auto' }}>
                <table className="table table-sm table-hover mb-0 text-center" data-testid="draft-table">
                  <thead>
                    <tr>
                      <th className="text-left pl-3">SKU Name</th>
                      <th>Qty</th>
                      <th>UoM</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody data-testid="draft-tbody">
                    {draftItems.length === 0 && (
                      <tr data-testid="draft-empty-row">
                        <td colSpan="4" className="text-muted py-4">No items added yet.</td>
                      </tr>
                    )}
                    {draftItems.map((d, idx) => {
                      const it = itemById[d.item_id];
                      return (
                        <tr key={idx} data-testid={`draft-row-${idx}`}>
                          <td className="text-left pl-3">{it ? `${it.item_code} - ${it.name}` : d.item_id}</td>
                          <td data-testid={`draft-qty-${idx}`}>{d.quantity}</td>
                          <td>{d.unit || it?.uom || '--'}</td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeDraftItem(idx)}
                              data-testid={`draft-remove-${idx}`}
                            >
                              <i className="fa-solid fa-trash" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Summary */}
              {draftItems.length > 0 && (
                <div className="border rounded p-3 bg-light mt-3" data-testid="order-summary">
                  <div className="row text-center">
                    <div className="col-6 border-right">
                      <small className="text-muted d-block">Distinct SKUs</small>
                      <span className="h5 font-weight-bold text-teal mb-0" data-testid="summary-distinct-skus">{draftItems.length}</span>
                    </div>
                    <div className="col-6">
                      <small className="text-muted d-block">Total Item Units</small>
                      <span className="h5 font-weight-bold text-teal mb-0" data-testid="summary-total-qty">{totalQty}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Special instructions */}
              <div className="form-group mt-3">
                <label className="font-weight-600 text-dark">Delivery Special Instructions</label>
                <textarea
                  className="form-control"
                  rows="2"
                  maxLength="300"
                  placeholder="Loading notes, timing preferences... (max 300 chars)"
                  value={form.special_instructions}
                  onChange={(e) => setField('special_instructions', e.target.value)}
                  data-testid="special-instructions"
                />
              </div>

              <button
                type="button"
                className="btn btn-primary btn-block btn-lg mt-2 shadow-sm"
                onClick={submitOrder}
                disabled={submitting || draftItems.length === 0 || !form.outlet_id || !form.delivery_date}
                data-testid="btn-submit-order"
              >
                <i className="fa-solid fa-paper-plane mr-2" />
                {submitting ? 'Submitting...' : 'Submit Pull Order'}
              </button>
            </div>
          </div>
        </div>}

        {/* Right: History & Tracking */}
        <div className={`${isBrandManager ? 'col-12' : 'col-xl-7 col-lg-6'} mb-4`}>
          <div className="card h-100 shadow-sm">
            <div className="card-header bg-white py-3 border-bottom">
              <h5 className="card-title mb-0 font-weight-bold text-dark">
                <i className="fa-solid fa-history mr-2 text-primary" />Pull Order History &amp; Real-Time Tracking
              </h5>
            </div>
            <div className="card-body">
              {/* Filters */}
              <div className="row mb-3">
                <div className="col-md-4 mb-2">
                  <select
                    className="form-control form-control-sm custom-select"
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    data-testid="filter-status"
                  >
                    <option value="ALL">All Statuses</option>
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-5 mb-2">
                  <div className="input-group input-group-sm">
                    <input type="date" className="form-control" value={filterDateStart}
                      onChange={(e) => setFilterDateStart(e.target.value)} data-testid="filter-date-start" />
                    <div className="input-group-prepend input-group-append">
                      <span className="input-group-text border-left-0 border-right-0 bg-white">to</span>
                    </div>
                    <input type="date" className="form-control" value={filterDateEnd}
                      onChange={(e) => setFilterDateEnd(e.target.value)} data-testid="filter-date-end" />
                  </div>
                </div>
                <div className="col-md-3 mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search Order / SKU..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="order-search"
                  />
                </div>
              </div>

              {/* Orders table */}
              <div className="table-responsive" data-testid="orders-table-wrap">
                <table className="table table-hover mb-0 text-center" data-testid="orders-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th className="text-left">Outlet</th>
                      <th>Date</th>
                      <th>SKUs</th>
                      <th>Total Qty</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOrders.length === 0 && (
                      <tr><td colSpan="7" className="text-muted py-4" data-testid="orders-empty">No orders found.</td></tr>
                    )}
                    {filteredOrders.map((o) => (
                      <tr key={o.id} data-testid={`order-row-${o.id}`}>
                        <td>
                          <small className="text-muted" data-testid={`order-id-${o.id}`}>{o.id.slice(0, 8)}…</small>
                          {o.has_duplicate_warning && (
                            <span className="badge badge-warning ml-1" data-testid={`dup-warn-${o.id}`} title="Duplicate warning">DUP</span>
                          )}
                          {o.has_late_warning && (
                            <span className="badge badge-danger ml-1" data-testid={`late-warn-${o.id}`} title="Late submission">LATE</span>
                          )}
                        </td>
                        <td className="text-left" data-testid={`order-outlet-${o.id}`}>{o.outlet_name}</td>
                        <td data-testid={`order-date-${o.id}`}>{o.delivery_date}</td>
                        <td data-testid={`order-skus-${o.id}`}>{(o.items || []).length}</td>
                        <td data-testid={`order-qty-${o.id}`}>{o.total_qty}</td>
                        <td>
                          <span className={`badge ${statusBadgeClass(o.status)}`} data-testid={`order-status-${o.id}`}>
                            {o.status}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => openDetail(o)}
                            data-testid={`order-detail-${o.id}`}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detail Modal (Bootstrap 4 — toggled via state + inline style) */}
      {modalOrder && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          data-testid="order-detail-modal"
        >
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header bg-teal text-white py-3">
                <h5 className="modal-title font-weight-bold">
                  <i className="fa-solid fa-receipt mr-2" />Pull Order Details
                </h5>
                <button type="button" className="close text-white" onClick={() => setModalOrder(null)} data-testid="modal-close">
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body p-4">
                <div className="row mb-4">
                  <div className="col-md-6 border-right">
                    <h6 className="font-weight-bold text-teal mb-3">Header Information</h6>
                    <table className="table table-sm table-borderless mb-0">
                      <tbody>
                        <tr>
                          <th className="text-muted" style={{ width: '40%', fontWeight: 'normal', fontSize: '0.85rem' }}>Order ID:</th>
                          <td><strong data-testid="modal-order-id">{modalOrder.id}</strong></td>
                        </tr>
                        <tr>
                          <th className="text-muted" style={{ fontWeight: 'normal', fontSize: '0.85rem' }}>Outlet:</th>
                          <td data-testid="modal-outlet">{modalOrder.outlet_name}</td>
                        </tr>
                        <tr>
                          <th className="text-muted" style={{ fontWeight: 'normal', fontSize: '0.85rem' }}>Cadence:</th>
                          <td><span className="badge badge-info" data-testid="modal-cadence">{modalOrder.cadence}</span></td>
                        </tr>
                        <tr>
                          <th className="text-muted" style={{ fontWeight: 'normal', fontSize: '0.85rem' }}>Delivery Date:</th>
                          <td data-testid="modal-delivery-date">{modalOrder.delivery_date}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6 pl-md-4">
                    <h6 className="font-weight-bold text-teal mb-3">Fulfillment Progress</h6>
                    <table className="table table-sm table-borderless mb-0">
                      <tbody>
                        <tr>
                          <th className="text-muted" style={{ width: '40%', fontWeight: 'normal', fontSize: '0.85rem' }}>Submitted On:</th>
                          <td data-testid="modal-submitted-date">{new Date(modalOrder.created_at).toLocaleString()}</td>
                        </tr>
                        <tr>
                          <th className="text-muted" style={{ fontWeight: 'normal', fontSize: '0.85rem' }}>Status:</th>
                          <td>
                            <span className={`badge ${statusBadgeClass(modalOrder.status)}`} data-testid="modal-status-badge">
                              {modalOrder.status}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {/* Warnings summary */}
                    <div className="mt-2">
                      {modalOrder.has_duplicate_warning && (
                        <div className="alert alert-warning py-1 px-2 mb-1" style={{ fontSize: '0.8rem' }} data-testid="modal-dup-warning">
                          Duplicate order warning. Ref: {modalOrder.duplicate_ref}
                        </div>
                      )}
                      {modalOrder.has_late_warning && (
                        <div className="alert alert-danger py-1 px-2 mb-1" style={{ fontSize: '0.8rem' }} data-testid="modal-late-warning">
                          Submitted after cut-off time.
                        </div>
                      )}
                      {modalOrder.has_no_cutoff_warning && (
                        <div className="alert alert-secondary py-1 px-2 mb-1" style={{ fontSize: '0.8rem' }} data-testid="modal-no-cutoff-warning">
                          No cut-off rule configured — administrative exception.
                        </div>
                      )}
                    </div>
                    <div className="mt-2">
                      <label className="text-muted mb-1 d-block" style={{ fontSize: '0.85rem' }}>Delivery Instructions:</label>
                      <div className="bg-light p-2 rounded text-dark" style={{ fontSize: '0.85rem', minHeight: 45 }} data-testid="modal-instructions">
                        {modalOrder.special_instructions || 'None provided.'}
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="my-3" />

                <h6 className="font-weight-bold text-dark mb-2">Compiled Items Checklist</h6>
                <div className="table-responsive border rounded bg-white">
                  <table className="table table-sm table-hover mb-0 text-center">
                    <thead>
                      <tr>
                        <th className="text-left pl-3">SKU Code &amp; Name</th>
                        <th>Category</th>
                        <th>Requested Qty</th>
                        <th>UoM</th>
                      </tr>
                    </thead>
                    <tbody data-testid="modal-sku-tbody">
                      {(modalOrder.items || []).map((it, idx) => (
                        <tr key={it.id || idx} data-testid={`modal-sku-row-${idx}`}>
                          <td className="text-left pl-3">{it.item_code} — {it.item_name}</td>
                          <td>{it.category}</td>
                          <td data-testid={`modal-sku-qty-${idx}`}>{it.quantity}</td>
                          <td>{it.uom}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Lifecycle error */}
                {lifecycleError && (
                  <div className="alert alert-danger mt-3 py-2" data-testid="lifecycle-error">{lifecycleError}</div>
                )}

                {/* UC 34271 — Acknowledge (supervisor only, not brand manager read-only view) */}
                {isSupervisor && !isBrandManager && modalOrder?.status === 'Submitted' && (
                  <div className="bg-light p-3 rounded mt-3 border" data-testid="acknowledge-panel">
                    <h6 className="font-weight-bold text-dark mb-2">
                      <i className="fa-solid fa-check-circle mr-1 text-success" />Acknowledge Order
                    </h6>
                    <button
                      type="button"
                      className="btn btn-success btn-block"
                      onClick={acknowledgeOrder}
                      disabled={lifecycleLoading}
                      data-testid="btn-acknowledge"
                    >
                      Acknowledge Pull Order
                    </button>
                  </div>
                )}

                {/* UC 34273 — Reject (supervisor only, not brand manager) */}
                {isSupervisor && !isBrandManager && ['Submitted', 'Acknowledged'].includes(modalOrder?.status) && (
                  <div className="bg-light p-3 rounded mt-3 border" data-testid="reject-panel">
                    <h6 className="font-weight-bold text-dark mb-2">
                      <i className="fa-solid fa-ban mr-1 text-danger" />Reject Order
                    </h6>
                    <textarea
                      className="form-control mb-2"
                      rows="2"
                      placeholder="Enter rejection reason (required)..."
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      data-testid="reject-reason-input"
                    />
                    <button
                      type="button"
                      className="btn btn-danger btn-block"
                      onClick={rejectOrder}
                      disabled={lifecycleLoading}
                      data-testid="btn-reject"
                    >
                      Reject Pull Order
                    </button>
                  </div>
                )}

                {/* Rejection reason display (for Rejected orders) */}
                {modalOrder?.status === 'Rejected' && modalOrder.rejection_reason && (
                  <div className="alert alert-danger mt-3 py-2" data-testid="modal-rejection-reason">
                    <strong>Rejection Reason:</strong> {modalOrder.rejection_reason}
                  </div>
                )}

                {/* UC 34273 — Resubmit (outlet manager, Rejected orders) */}
                {isManager && modalOrder?.status === 'Rejected' && (
                  <div className="bg-light p-3 rounded mt-3 border" data-testid="resubmit-panel">
                    <h6 className="font-weight-bold text-dark mb-2">
                      <i className="fa-solid fa-rotate-right mr-1 text-primary" />Resubmit as New Draft
                    </h6>
                    <p className="text-muted small mb-2">Creates a new editable draft from this rejected order. The original record is preserved for audit.</p>
                    <button
                      type="button"
                      className="btn btn-primary btn-block"
                      onClick={resubmitOrder}
                      disabled={lifecycleLoading}
                      data-testid="btn-resubmit"
                    >
                      Create Resubmission Draft
                    </button>
                  </div>
                )}

                {/* UC 34275 — Cancel (outlet manager OR supervisor, not brand manager, Submitted only) */}
                {(isManager || isSupervisor) && !isBrandManager && modalOrder?.status === 'Submitted' && (
                  <div className="bg-light p-3 rounded mt-3 border" data-testid="cancel-panel">
                    <h6 className="font-weight-bold text-dark mb-2">
                      <i className="fa-solid fa-circle-xmark mr-1 text-warning" />Cancel Order
                    </h6>
                    <textarea
                      className="form-control mb-2"
                      rows="2"
                      placeholder="Enter cancellation reason (required)..."
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      data-testid="cancel-reason-input"
                    />
                    <button
                      type="button"
                      className="btn btn-warning btn-block"
                      onClick={cancelOrder}
                      disabled={lifecycleLoading}
                      data-testid="btn-cancel-order"
                    >
                      Cancel Pull Order
                    </button>
                  </div>
                )}

                {/* Cancellation reason display */}
                {modalOrder?.status === 'Cancelled' && modalOrder.cancellation_reason && (
                  <div className="alert alert-warning mt-3 py-2" data-testid="modal-cancellation-reason">
                    <strong>Cancellation Reason:</strong> {modalOrder.cancellation_reason}
                  </div>
                )}

                {/* Generic supervisor action (non-lifecycle statuses, not brand manager) */}
                {isSupervisor && !isBrandManager && !['Submitted', 'Acknowledged', 'Rejected', 'Cancelled'].includes(modalOrder?.status) && (
                  <div className="bg-light p-3 rounded mt-3 border" data-testid="supervisor-action-panel">
                    <h6 className="font-weight-bold text-dark mb-2">Supervisor Action Desk</h6>
                    <div className="form-row align-items-center">
                      <div className="col-sm-6 mb-2 mb-sm-0">
                        <select
                          className="form-control custom-select"
                          value={supervisorStatus}
                          onChange={(e) => setSupervisorStatus(e.target.value)}
                          data-testid="supervisor-action-status"
                        >
                          {STATUS_OPTIONS.filter((s) => !['Submitted', 'Acknowledged', 'Rejected', 'Cancelled'].includes(s)).map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                      <div className="col-sm-6">
                        <button
                          type="button"
                          className="btn btn-primary btn-block"
                          onClick={saveStatusUpdate}
                          disabled={statusUpdating}
                          data-testid="btn-save-supervisor-action"
                        >
                          <i className="fa-solid fa-save mr-1" />Update Order Status
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary px-4" onClick={() => setModalOrder(null)} data-testid="modal-close-footer">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
